import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { Prijava } from './ekrani/Prijava'
import { Katalog } from './ekrani/Katalog'
import { Korisnici } from './ekrani/Korisnici'
import { Prijem } from './ekrani/Prijem'
import { PromjenaLozinke } from './ekrani/PromjenaLozinke'
import { Navigacija, type Ekran } from './komponente/Navigacija'
import type { Nalog, Uloga } from './lib/tipovi'

const nazivUloge: Record<Uloga, string> = {
  admin: 'Admin',
  magacioner: 'Magacioner',
  vozac: 'Vozač',
  citalac: 'Čitalac',
  knjigovodja: 'Knjigovođa',
}

const naslovEkrana: Record<Ekran, string> = {
  katalog: 'Katalog',
  korisnici: 'Korisnici',
  prijem: 'Prijem',
}

export function App() {
  const [sesija, postaviSesiju] = useState<Session | null>(null)
  const [sesijaProvjerena, postaviProvjereno] = useState(false)
  const [nalog, postaviNalog] = useState<Nalog | null>(null)
  const [greska, postaviGresku] = useState<string | null>(null)
  const [ekran, postaviEkran] = useState<Ekran>('katalog')

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      postaviSesiju(data.session)
      postaviProvjereno(true)
    })
    // Time-box smjene je 12h (DE-2) i sprovodi ga Supabase Auth: kad sesija istekne,
    // ovaj slušalac vrati ekran na prijavu, bez sopstvenog brojača.
    const { data } = supabase.auth.onAuthStateChange((_dogadjaj, novaSesija) => {
      postaviSesiju(novaSesija)
      postaviProvjereno(true)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!sesija) {
      postaviNalog(null)
      return
    }
    let otkazano = false

    // Satovi GoTrue-a i PostgREST-a znaju da se raziđu za koji sekund, pa upit poslat
    // odmah po prijavi umije da padne sa „JWT issued at future". Jedan tihi pokušaj više
    // to preskače, umjesto da korisnik gleda grešku koja sama prolazi.
    async function procitajProfil() {
      const upit = () =>
        supabase
          .from('profili')
          .select(
            'ime, aktivna_firma_id, mora_promijeniti_lozinku, firme:aktivna_firma_id (naziv)',
          )
          .eq('id', sesija!.user.id)
          .maybeSingle()

      const prvi = await upit()
      if (!prvi.error) return prvi
      await new Promise((r) => setTimeout(r, 1200))
      return upit()
    }

    async function ucitajNalog() {
      const [odgovorProfila, odgovorUloge] = await Promise.all([
        procitajProfil(),
        supabase.rpc('moja_uloga'),
      ])

      if (otkazano) return

      // Greška u prenosu nije isto što i nalog bez firme. Ranije je svaka greška
      // prijavljivana kao „nema profil", pa je razminut sat izgledao kao pogrešan nalog.
      if (odgovorProfila.error) {
        postaviGresku(`Profil se ne može učitati: ${odgovorProfila.error.message}`)
        return
      }

      // Nalog bez profila i članstva stvarno ne vidi ništa: RLS mu vraća prazno, pa je
      // poštenije to reći nego pustiti prazan katalog (C25).
      if (!odgovorProfila.data) {
        postaviGresku('Nalog nema profil ni firmu. Dodaje ih admin kroz pozivnicu (C25).')
        return
      }

      const red = odgovorProfila.data as unknown as {
        ime: string
        aktivna_firma_id: string
        mora_promijeniti_lozinku: boolean
        firme: { naziv: string } | { naziv: string }[] | null
      }
      const firma = Array.isArray(red.firme) ? red.firme[0] : red.firme

      postaviNalog({
        korisnikId: sesija!.user.id,
        ime: red.ime,
        firmaId: red.aktivna_firma_id,
        firmaNaziv: firma?.naziv ?? '—',
        uloga: (odgovorUloge.data as Uloga | null) ?? null,
        moraPromijenitiLozinku: red.mora_promijeniti_lozinku,
      })
    }

    void ucitajNalog()
    return () => {
      otkazano = true
    }
  }, [sesija])

  if (!sesijaProvjerena) {
    return <div className="ucitavanje">Učitavanje…</div>
  }

  if (!sesija) {
    return <Prijava />
  }

  // Privremena lozinka se mora promijeniti prije svega ostalog (EOV-7A). Kapija stoji
  // ovdje, a ne u Prijavi: zastavica se čita iz profila, koji postoji tek poslije sesije.
  // Dok se profil učitava ne pušta se aplikacija, inače bi katalog bljesnuo na trenutak.
  if (nalog === null && greska === null) {
    return <div className="ucitavanje">Učitavanje…</div>
  }

  if (nalog?.moraPromijenitiLozinku) {
    return (
      <PromjenaLozinke
        ime={nalog.ime}
        naPromjenu={() => postaviNalog({ ...nalog, moraPromijenitiLozinku: false })}
      />
    )
  }

  return (
    <div className="okvir">
      <Navigacija aktivno={ekran} naIzbor={postaviEkran} />
      <main className="sadrzaj">
        <div className="traka">
          <div>
            <h1 className="prikaz">{naslovEkrana[ekran]}</h1>
            <div className="oznaka tabela__tiho" data-testid="traka-firma">
              {nalog?.firmaNaziv ?? '…'}
            </div>
          </div>
          <div className="traka__desno">
            <span className="traka__korisnik" data-testid="traka-korisnik">
              {nalog ? `${nalog.ime}${nalog.uloga ? ` · ${nazivUloge[nalog.uloga]}` : ''}` : '…'}
            </span>
            <button
              type="button"
              className="veza-tekst"
              data-testid="dugme-odjava"
              onClick={() => void supabase.auth.signOut()}
            >
              Odjava
            </button>
          </div>
        </div>

        {greska && (
          <div className="greska" role="alert" data-testid="nalog-greska">
            {greska}
          </div>
        )}

        {ekran === 'katalog' && <Katalog nalog={nalog} />}
        {ekran === 'prijem' && <Prijem nalog={nalog} />}
        {ekran === 'korisnici' && <Korisnici nalog={nalog} />}
      </main>
    </div>
  )
}
